import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { 
  Link2, 
  Loader2, 
  CheckCircle, 
  AlertCircle, 
  FileText, 
  Sparkles,
  ChevronDown,
  ChevronUp,
  Plus,
  Save,
  Eye,
  RefreshCw,
  X,
  Copy,
  Check,
  Layers,
  AlertTriangle
} from 'lucide-react';
import axios from 'axios';
import { DAY_THEME_OPTIONS } from '../../data/dayThemes';
import BatchIngestLane from './BatchIngestLane';

// Detect a missing-space concatenation (lowercase→UPPERCASE boundary) → split suggestion.
// e.g. "theWORLD" → "the WORLD", "toYHWH" → "to YHWH", "willINSPIYAH" → "will INSPIYAH"
const splitConcatenation = (word) => {
  const s = (word || '').replace(/([a-z])([A-Z])/g, '$1 $2');
  return s !== word ? s : null;
};

// One spelling issue row: Split-fix + inline manual edit + suggestion chips + Ignore + Brand Dict
const SpellingErrorRow = ({ error, onFix, onIgnore, onAddDict }) => {
  const split = splitConcatenation(error.word);
  const [manual, setManual] = useState(split || error.word);

  return (
    <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-100" data-testid={`spelling-error-${error.word}`}>
      <div className="flex items-center justify-between">
        <p className="font-medium text-yellow-800">{error.word}</p>
        <div className="flex items-center gap-1">
          <button
            onClick={() => onAddDict(error.word, true)}
            className="text-[11px] px-2 py-1 rounded bg-purple-100 text-purple-700 hover:bg-purple-200 font-medium"
            title="Add to Brand Dictionary (intentional spelling / acronym)"
            data-testid={`spelling-add-dict-${error.word}`}
          >
            + Brand Dict
          </button>
          <button
            onClick={() => onIgnore(error.word)}
            className="text-[11px] px-2 py-1 rounded bg-gray-100 text-gray-600 hover:bg-gray-200 font-medium"
            title="Ignore this issue for now"
            data-testid={`spelling-ignore-${error.word}`}
          >
            Ignore
          </button>
        </div>
      </div>
      <p className="text-xs text-gray-500 mt-1">{error.context}</p>

      {/* Suggestions: auto-split (if detected) + dictionary suggestions */}
      {(split || error.suggestions.length > 0) && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          <span className="text-xs text-gray-500 self-center">Fix:</span>
          {split && (
            <button
              onClick={() => onFix(error.word, split, 'split')}
              className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700 hover:bg-blue-200 font-medium"
              title={`Add the missing space → "${split}"`}
              data-testid={`spelling-split-${error.word}`}
            >
              ↔ {split}
            </button>
          )}
          {error.suggestions.map((sug, i) => (
            <button
              key={i}
              onClick={() => onFix(error.word, sug, 'suggestion')}
              className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 hover:bg-green-200 font-medium"
              title={`Replace "${error.word}" with "${sug}"`}
              data-testid={`spelling-fix-${error.word}-${sug}`}
            >
              {sug}
            </button>
          ))}
        </div>
      )}

      {/* Inline manual edit — type any correction and Apply */}
      <div className="flex items-center gap-2 mt-2">
        <input
          type="text"
          value={manual}
          onChange={(e) => setManual(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') onFix(error.word, manual, 'manual'); }}
          className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded text-gray-900 bg-white"
          placeholder="Type the correction…"
          data-testid={`spelling-manual-input-${error.word}`}
        />
        <button
          onClick={() => onFix(error.word, manual, 'manual')}
          className="text-[11px] px-3 py-1.5 rounded bg-yellow-600 text-white hover:bg-yellow-700 font-medium whitespace-nowrap"
          title="Replace this word in the article with your text"
          data-testid={`spelling-manual-apply-${error.word}`}
        >
          Apply
        </button>
      </div>
    </div>
  );
};

const DAILY_THEMES = [
  { value: '', label: '— No daily theme —' },
  ...DAY_THEME_OPTIONS,
];

const ArticleImporter = () => {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [importedArticle, setImportedArticle] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [editedContent, setEditedContent] = useState('');
  const [editedTitle, setEditedTitle] = useState('');
  const [editedExcerpt, setEditedExcerpt] = useState('');
  const [selectedImage, setSelectedImage] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [saving, setSaving] = useState(false);
  const [copiedTemplate, setCopiedTemplate] = useState(null);
  const [importHistory, setImportHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [addedTemplates, setAddedTemplates] = useState([]);
  const [checkedTemplates, setCheckedTemplates] = useState({});
  const [spellingErrors, setSpellingErrors] = useState([]);
  const [brandWords, setBrandWords] = useState([]);
  const [showDict, setShowDict] = useState(false);
  const [newDictWord, setNewDictWord] = useState('');
  const [fixLog, setFixLog] = useState([]);
  const [dailyTheme, setDailyTheme] = useState('');
  const [duplicateInfo, setDuplicateInfo] = useState(null);
  const [mode, setMode] = useState('single');
  
  const backendUrl = '';

  useEffect(() => {
    fetchTemplates();
    fetchHistory();
    fetchBrandDictionary();
  }, []);

  const fetchBrandDictionary = async () => {
    try {
      const res = await axios.get(`${backendUrl}/api/admin/import/brand-dictionary`);
      setBrandWords(res.data.words || []);
    } catch (error) {
      console.error('Error fetching brand dictionary:', error);
    }
  };

  // Record a spelling resolution (categorized) for the live counter + changelog
  const logFix = (word, action, from, to) => {
    setFixLog(prev => [...prev, { word, action, from: from ?? word, to: to ?? null, ts: new Date().toISOString() }]);
  };

  // Replace whole-word occurrences (case-insensitive) of a flagged word in the content.
  // action: 'split' | 'suggestion' | 'manual'
  const applySuggestion = (word, suggestion, action = 'suggestion') => {
    const re = new RegExp(`\\b${word.replace(/[.*+?^${}()|[\\]\\\\]/g, '\\$&')}\\b`, 'gi');
    setEditedContent(prev => prev.replace(re, suggestion));
    setSpellingErrors(prev => prev.filter(e => e.word.toLowerCase() !== word.toLowerCase()));
    logFix(word, action, word, suggestion);
  };

  const ignoreSpellingError = (word) => {
    setSpellingErrors(prev => prev.filter(e => e.word.toLowerCase() !== word.toLowerCase()));
    logFix(word, 'ignored', word, null);
  };

  const addToBrandDictionary = async (word, fromError = false) => {
    try {
      const res = await axios.post(`${backendUrl}/api/admin/import/brand-dictionary`, { word });
      setBrandWords(res.data.words || []);
      setSpellingErrors(prev => prev.filter(e => e.word.toLowerCase() !== word.toLowerCase()));
      if (fromError) logFix(word, 'brand_dict', word, null);
    } catch (error) {
      console.error('Error adding to brand dictionary:', error);
      alert('Could not add word to Brand Dictionary.');
    }
  };

  const addManualDictWord = async () => {
    const w = newDictWord.trim();
    if (!w) return;
    await addToBrandDictionary(w);
    setNewDictWord('');
  };

  const removeBrandWord = async (word) => {
    try {
      const res = await axios.delete(`${backendUrl}/api/admin/import/brand-dictionary/${encodeURIComponent(word)}`);
      setBrandWords(res.data.words || []);
    } catch (error) {
      console.error('Error removing brand word:', error);
    }
  };

  // Live read time from current edited content (~200 wpm)
  const getReadTime = () => {
    const words = (editedContent || '').replace(/<[^>]*>/g, ' ').trim().split(/\s+/).filter(Boolean).length;
    return Math.max(1, Math.ceil(words / 200));
  };

  const fetchTemplates = async () => {
    try {
      // Fetch from the new Template Manager API (database-backed)
      const response = await axios.get(`${backendUrl}/api/admin/templates/`);
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/admin/import/history`);
      setImportHistory(response.data);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const handleImport = async () => {
    if (!url.trim()) {
      alert('Please enter a URL');
      return;
    }

    setLoading(true);
    setImportedArticle(null);
    setDuplicateInfo(null);

    try {
      const response = await axios.post(`${backendUrl}/api/admin/import/scrape`, { url });
      setImportedArticle(response.data);
      setEditedContent(response.data.scraped_data.content);
      setEditedTitle(response.data.scraped_data.title);
      setEditedExcerpt(response.data.scraped_data.excerpt);
      setSpellingErrors(response.data.quality_analysis?.spelling_errors || []);
      setFixLog([]);
      if (response.data.is_duplicate) {
        setDuplicateInfo({ id: response.data.duplicate_of });
      }
      if (response.data.scraped_data.images.length > 0) {
        setSelectedImage(response.data.scraped_data.images[0]);
      }
    } catch (error) {
      console.error('Error importing article:', error);
      alert('Failed to import article. Please check the URL and try again.');
    } finally {
      setLoading(false);
    }
  };

  const fillTokens = (text) => {
    if (!dailyTheme) return text;
    return text.replace(/\[DAY OF THE WEEK\]/gi, dailyTheme);
  };

  const addTemplateToContent = (templateName, templateContent) => {
    setEditedContent(prev => prev + '\n\n' + fillTokens(templateContent));
    setAddedTemplates(prev => [...prev, templateName]);
  };

  const toggleTemplateCheck = (templateId) => {
    setCheckedTemplates(prev => ({
      ...prev,
      [templateId]: !prev[templateId]
    }));
  };

  const getSortedTemplates = () => {
    return [...templates].sort((a, b) => {
      const getOrder = (name) => {
        const match = name.match(/^Section\s+(\d+)/i);
        if (match) return parseFloat(match[1]);
        if (name.startsWith('TBD')) return 1000;
        return 999;
      };
      return getOrder(a.name) - getOrder(b.name);
    });
  };

  const buildAssembledContent = (baseContent) => {
    const selected = getSortedTemplates().filter(t => checkedTemplates[t.id]);
    if (selected.length === 0) return baseContent;
    const beforeBody = [];
    const afterBody = [];
    selected.forEach(t => {
      const match = t.name.match(/^Section\s+(\d+)/i);
      const num = match ? parseInt(match[1]) : 999;
      if (num <= 1) beforeBody.push(t);
      else if (num >= 2) afterBody.push(t); // Section 2 is the body; 3+ go after
    });
    const parts = [];
    beforeBody.forEach(t => parts.push(fillTokens(t.content)));
    parts.push(baseContent); // Body (imported article)
    afterBody.forEach(t => parts.push(fillTokens(t.content)));
    return parts.join('\n\n');
  };

  const assembleArticle = () => {
    const selected = getSortedTemplates().filter(t => checkedTemplates[t.id]);
    if (selected.length === 0) {
      alert('Please select at least one section to assemble');
      return;
    }
    setEditedContent(prev => buildAssembledContent(prev));
    setAddedTemplates(selected.map(t => t.name));
    setCheckedTemplates({});
  };

  const copyTemplateToClipboard = async (templateId, content) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedTemplate(templateId);
      setTimeout(() => setCopiedTemplate(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleSave = async (publish = false) => {
    if (!editedTitle.trim()) {
      alert('Please enter a title');
      return;
    }

    setSaving(true);

    try {
      // Auto-apply any selected-but-not-yet-assembled template sections so
      // checked templates always end up in the saved article.
      const pendingSelected = getSortedTemplates().filter(t => checkedTemplates[t.id]);
      const finalContent = buildAssembledContent(editedContent);
      const sectionsAdded = pendingSelected.length > 0
        ? [...addedTemplates, ...pendingSelected.map(t => t.name)]
        : addedTemplates;

      const slug = editedTitle
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-');

      const articleData = {
        title: editedTitle,
        subtitle: '',
        slug: slug,
        category: 'Spirituality',
        author: importedArticle?.scraped_data?.author || 'Keeper Of The Fiyah',
        date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
        readTime: `${getReadTime()} min read`,
        image: selectedImage,
        excerpt: editedExcerpt,
        content: finalContent,
        sourceUrl: url,
        dailyTheme: dailyTheme,
        published: publish
      };

      const saveResponse = await axios.post(`${backendUrl}/api/admin/import/save`, articleData);

      // Log to changelog
      try {
        await axios.post(`${backendUrl}/api/admin/changelog/log`, {
          article_id: saveResponse.data.id,
          article_title: editedTitle,
          action_type: 'IMPORT',
          user: 'Admin',
          before_snapshot: null,
          after_snapshot: {
            title: editedTitle,
            excerpt: editedExcerpt,
            content: finalContent,
            image: selectedImage,
            published: publish
          },
          changes_summary: [
            'Imported from external source',
            `Word count: ${finalContent.split(/\s+/).length}`,
            publish ? 'Published immediately' : 'Saved as draft'
          ],
          template_sections_added: sectionsAdded,
          source_url: url,
          metadata: {
            word_count_after: finalContent.split(/\s+/).length,
            spelling_score: importedArticle?.quality_analysis?.spelling_score || 0,
            completeness_score: importedArticle?.quality_analysis?.completeness_score || 0
          }
        });
      } catch (logError) {
        console.error('Error logging to changelog:', logError);
      }

      // Log spelling resolutions (categorized) to the changelog
      if (fixLog.length > 0) {
        const byAction = fixLog.reduce((acc, f) => { acc[f.action] = (acc[f.action] || 0) + 1; return acc; }, {});
        const ACTION_LABELS = { split: 'missing-space split', suggestion: 'suggestion applied', manual: 'manual fix', brand_dict: 'added to Brand Dictionary', ignored: 'ignored' };
        try {
          await axios.post(`${backendUrl}/api/admin/changelog/log`, {
            article_id: saveResponse.data.id,
            article_title: editedTitle,
            action_type: 'SPELLING_FIXED',
            user: 'Admin',
            changes_summary: [
              `${fixLog.length} spelling issue(s) resolved`,
              ...Object.entries(byAction).map(([a, n]) => `${n} ${ACTION_LABELS[a] || a}`),
            ],
            source_url: url,
            metadata: {
              resolved_total: fixLog.length,
              by_action: byAction,
              details: fixLog.map(f => ({ word: f.word, action: f.action, to: f.to })),
            }
          });
        } catch (spellLogErr) {
          console.error('Error logging spelling fixes:', spellLogErr);
        }
      }

      alert(`Article ${publish ? 'published' : 'saved as draft'} successfully!\n\nView it at: /article/${slug}`);
      
      // Reset form
      setUrl('');
      setImportedArticle(null);
      setEditedContent('');
      setEditedTitle('');
      setEditedExcerpt('');
      setSelectedImage('');
      setAddedTemplates([]);
      setCheckedTemplates({});
      setSpellingErrors([]);
      setFixLog([]);
      setDailyTheme('');
      setDuplicateInfo(null);
      fetchHistory();
    } catch (error) {
      console.error('Error saving article:', error);
      alert('Failed to save article. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBg = (score) => {
    if (score >= 90) return 'bg-green-100';
    if (score >= 70) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  return (
    <div className="space-y-6">
      {/* Lane switch: Single Import vs Batch Ingest */}
      <div className="inline-flex rounded-lg bg-gray-100 p-1" data-testid="importer-mode-switch">
        <button
          onClick={() => setMode('single')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${mode === 'single' ? 'bg-white shadow text-purple-700' : 'text-gray-500 hover:text-gray-700'}`}
          data-testid="mode-single-btn"
        >
          Single Import
        </button>
        <button
          onClick={() => setMode('batch')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${mode === 'batch' ? 'bg-white shadow text-indigo-700' : 'text-gray-500 hover:text-gray-700'}`}
          data-testid="mode-batch-btn"
        >
          Batch Ingest
        </button>
      </div>

      {mode === 'batch' ? <BatchIngestLane /> : (
      <div className="space-y-6">
      {/* Import URL Section */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
            <Link2 className="text-white" size={20} />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900">Article Importer</h3>
            <p className="text-sm text-gray-500">Import articles from LinkedIn and other sources</p>
          </div>
        </div>

        <div className="flex space-x-4">
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="Paste article URL (e.g., LinkedIn article link)"
            className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 placeholder-gray-500"
            data-testid="import-url-input"
          />
          <select
            value={dailyTheme}
            onChange={(e) => setDailyTheme(e.target.value)}
            className="w-52 px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 bg-white"
            data-testid="daily-theme-select"
          >
            {DAILY_THEMES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
          <button
            onClick={handleImport}
            disabled={loading}
            className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-medium hover:shadow-lg transition-all disabled:opacity-50 flex items-center space-x-2"
            data-testid="import-button"
          >
            {loading ? (
              <>
                <Loader2 className="animate-spin" size={20} />
                <span>Importing...</span>
              </>
            ) : (
              <>
                <Sparkles size={20} />
                <span>Import & Analyze</span>
              </>
            )}
          </button>
        </div>

        {/* Duplicate warning (testing phase: warn but allow re-import) */}
        {duplicateInfo && (
          <div className="mt-4 flex items-start space-x-3 bg-amber-50 border border-amber-300 rounded-lg p-4" data-testid="duplicate-warning">
            <AlertTriangle className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
            <div className="text-sm text-amber-800">
              <p className="font-semibold">This URL has already been imported.</p>
              <p>You can still re-import it during testing. It will be saved as a separate copy on save.</p>
            </div>
          </div>
        )}

        {/* Import History Toggle */}
        <div className="mt-4">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="text-sm text-purple-600 hover:text-purple-700 flex items-center space-x-1"
          >
            <RefreshCw size={14} />
            <span>View Import History ({importHistory.length})</span>
            {showHistory ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          
          {showHistory && importHistory.length > 0 && (
            <div className="mt-3 max-h-48 overflow-y-auto border border-gray-200 rounded-lg">
              {importHistory.slice(0, 10).map((article, index) => (
                <div key={index} className="px-4 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{article.title}</p>
                  <p className="text-xs text-gray-500">{new Date(article.importedAt).toLocaleDateString()}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Results Section */}
      {importedArticle && (
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Quality Analysis Panel */}
          <div className="lg:col-span-1 space-y-4">
            {/* Scores */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center space-x-2">
                <CheckCircle className="text-green-500" size={20} />
                <span>Quality Analysis</span>
              </h4>

              <div className="space-y-4">
                {/* Spelling Score */}
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-medium text-gray-700">Spelling Score</span>
                    <span className={`text-lg font-bold ${getScoreColor(importedArticle.quality_analysis.spelling_score)}`}>
                      {importedArticle.quality_analysis.spelling_score}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${getScoreBg(importedArticle.quality_analysis.spelling_score).replace('bg-', 'bg-').replace('-100', '-500')}`}
                      style={{ width: `${importedArticle.quality_analysis.spelling_score}%` }}
                    />
                  </div>
                </div>

                {/* Completeness Score */}
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-medium text-gray-700">Template Completeness</span>
                    <span className={`text-lg font-bold ${getScoreColor(importedArticle.quality_analysis.completeness_score)}`}>
                      {importedArticle.quality_analysis.completeness_score}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${getScoreBg(importedArticle.quality_analysis.completeness_score).replace('bg-', 'bg-').replace('-100', '-500')}`}
                      style={{ width: `${importedArticle.quality_analysis.completeness_score}%` }}
                    />
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-3 pt-3 border-t border-gray-100">
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-gray-900">{importedArticle.quality_analysis.word_count}</p>
                    <p className="text-xs text-gray-500">Words</p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-gray-900">{importedArticle.quality_analysis.paragraph_count}</p>
                    <p className="text-xs text-gray-500">Paragraphs</p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg" data-testid="importer-readtime-stat">
                    <p className="text-2xl font-bold text-gray-900">{getReadTime()}</p>
                    <p className="text-xs text-gray-500">Min Read</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Spelling resolutions — live categorized counter (persists after list clears) */}
            {fixLog.length > 0 && (
              <div className="bg-white rounded-xl shadow-lg p-6" data-testid="spelling-resolved-summary">
                <h4 className="text-lg font-bold text-gray-900 flex items-center space-x-2 mb-1">
                  <CheckCircle className="text-green-500" size={20} />
                  <span>{fixLog.length} Issue{fixLog.length !== 1 ? 's' : ''} Resolved</span>
                </h4>
                <p className="text-xs text-gray-500 mb-3">{spellingErrors.length} remaining · logged to the Changelog on save</p>
                <div className="flex flex-wrap gap-2">
                  {(() => {
                    const meta = {
                      split: { label: 'Missing-space split', cls: 'bg-blue-100 text-blue-700' },
                      manual: { label: 'Manual fix', cls: 'bg-yellow-100 text-yellow-700' },
                      suggestion: { label: 'Suggestion applied', cls: 'bg-green-100 text-green-700' },
                      brand_dict: { label: 'Added to Brand Dictionary', cls: 'bg-purple-100 text-purple-700' },
                      ignored: { label: 'Ignored', cls: 'bg-gray-100 text-gray-600' },
                    };
                    const counts = fixLog.reduce((a, f) => { a[f.action] = (a[f.action] || 0) + 1; return a; }, {});
                    return Object.keys(meta)
                      .filter(k => counts[k])
                      .map(k => (
                        <span key={k} className={`text-xs px-2.5 py-1 rounded-full font-medium ${meta[k].cls}`} data-testid={`resolved-count-${k}`}>
                          {meta[k].label}: {counts[k]}
                        </span>
                      ));
                  })()}
                </div>
              </div>
            )}


            {/* Spelling Errors — interactive (Fix / Ignore / Add to Brand Dictionary) */}
            {spellingErrors.length > 0 && (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <button
                  onClick={() => toggleSection('spelling')}
                  className="w-full flex justify-between items-center"
                  data-testid="importer-spelling-toggle"
                >
                  <h4 className="text-lg font-bold text-gray-900 flex items-center space-x-2">
                    <AlertCircle className="text-yellow-500" size={20} />
                    <span>Spelling Issues ({spellingErrors.length})</span>
                  </h4>
                  {expandedSections.spelling ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>

                {expandedSections.spelling && (
                  <div className="mt-4 space-y-3 max-h-80 overflow-y-auto" data-testid="importer-spelling-list">
                    {spellingErrors.map((error, index) => (
                      <SpellingErrorRow
                        key={index}
                        error={error}
                        onFix={applySuggestion}
                        onIgnore={ignoreSpellingError}
                        onAddDict={addToBrandDictionary}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Brand Dictionary manager */}
            <div className="bg-white rounded-xl shadow-lg p-6" data-testid="brand-dictionary-panel">
              <button
                onClick={() => setShowDict(s => !s)}
                className="w-full flex justify-between items-center"
                data-testid="brand-dictionary-toggle"
              >
                <h4 className="text-lg font-bold text-gray-900 flex items-center space-x-2">
                  <FileText className="text-purple-500" size={20} />
                  <span>Brand Dictionary ({brandWords.length})</span>
                </h4>
                {showDict ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>
              {showDict && (
                <div className="mt-4">
                  <p className="text-xs text-gray-500 mb-3">Words here are treated as correct (intentional brand spellings &amp; acronyms) and skipped by the spellchecker on every import.</p>
                  <div className="flex gap-2 mb-3">
                    <input
                      type="text"
                      value={newDictWord}
                      onChange={(e) => setNewDictWord(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') addManualDictWord(); }}
                      placeholder="Add a word…"
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg text-gray-900"
                      data-testid="brand-dict-input"
                    />
                    <button
                      onClick={addManualDictWord}
                      className="px-3 py-2 text-sm rounded-lg bg-purple-600 text-white hover:bg-purple-700 font-medium"
                      data-testid="brand-dict-add-btn"
                    >
                      Add
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
                    {brandWords.map((w) => (
                      <span key={w.word} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-purple-50 text-purple-700 border border-purple-100">
                        {w.word}
                        <button onClick={() => removeBrandWord(w.word)} className="text-purple-400 hover:text-purple-700" title="Remove" data-testid={`brand-dict-remove-${w.word}`}>
                          <X size={12} />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Section Detection */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <button
                onClick={() => toggleSection('sections')}
                className="w-full flex justify-between items-center"
              >
                <h4 className="text-lg font-bold text-gray-900 flex items-center space-x-2">
                  <FileText className="text-blue-500" size={20} />
                  <span>Template Sections</span>
                </h4>
                {expandedSections.sections ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>

              {expandedSections.sections && (
                <div className="mt-4 space-y-2">
                  {importedArticle.quality_analysis.sections_detected.map((section, index) => (
                    <div 
                      key={index} 
                      className={`flex items-center justify-between p-2 rounded-lg ${
                        section.found ? 'bg-green-50' : section.required ? 'bg-red-50' : 'bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center space-x-2">
                        {section.found ? (
                          <CheckCircle className="text-green-500" size={16} />
                        ) : (
                          <X className={section.required ? 'text-red-500' : 'text-gray-400'} size={16} />
                        )}
                        <span className={`text-sm ${section.found ? 'text-green-700' : section.required ? 'text-red-700' : 'text-gray-600'}`}>
                          {section.display_name}
                        </span>
                      </div>
                      {section.required && (
                        <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">Required</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Template Assembly */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <button
                onClick={() => toggleSection('templates')}
                className="w-full flex justify-between items-center"
              >
                <h4 className="text-lg font-bold text-gray-900 flex items-center space-x-2">
                  <Layers className="text-purple-500" size={20} />
                  <span>Assemble Sections</span>
                </h4>
                {expandedSections.templates ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>

              {expandedSections.templates && (
                <div className="mt-4 space-y-2">
                  <p className="text-xs text-gray-500 mb-3">Select sections then click "Assemble" to auto-place them in order around your body content.</p>
                  
                  {getSortedTemplates().map((template) => {
                    const isSection2 = /^Section\s+2\b/i.test(template.name);
                    return (
                      <div key={template.id} className={`flex items-center space-x-2 p-2 rounded-lg ${checkedTemplates[template.id] ? 'bg-purple-50 border border-purple-200' : 'bg-gray-50 hover:bg-gray-100'} transition-colors`}>
                        <input
                          type="checkbox"
                          id={`tpl-${template.id}`}
                          checked={!!checkedTemplates[template.id]}
                          onChange={() => toggleTemplateCheck(template.id)}
                          disabled={isSection2}
                          className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                          data-testid={`template-checkbox-${template.id}`}
                        />
                        <label 
                          htmlFor={`tpl-${template.id}`}
                          className={`flex-1 text-sm cursor-pointer ${isSection2 ? 'text-gray-400 italic' : 'text-gray-700'}`}
                        >
                          {template.name}
                          {isSection2 && <span className="ml-2 text-xs text-purple-500">(= imported body)</span>}
                          {template.is_required && <span className="ml-2 text-xs px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded">Required</span>}
                        </label>
                        <button
                          onClick={() => copyTemplateToClipboard(template.id, template.content)}
                          className="p-1.5 hover:bg-gray-200 rounded transition-colors"
                          title="Copy to clipboard"
                        >
                          {copiedTemplate === template.id ? (
                            <Check size={14} className="text-green-500" />
                          ) : (
                            <Copy size={14} className="text-gray-400" />
                          )}
                        </button>
                      </div>
                    );
                  })}

                  {/* Assemble Button */}
                  <button
                    onClick={assembleArticle}
                    disabled={Object.values(checkedTemplates).filter(Boolean).length === 0}
                    className="w-full mt-4 flex items-center justify-center space-x-2 px-4 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg font-medium hover:shadow-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="assemble-article-btn"
                  >
                    <Layers size={18} />
                    <span>Assemble Article ({Object.values(checkedTemplates).filter(Boolean).length} sections)</span>
                  </button>

                  {addedTemplates.length > 0 && (
                    <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
                      <p className="text-xs font-medium text-green-700 mb-1">Assembled sections:</p>
                      <p className="text-xs text-green-600">{addedTemplates.join(' → ')}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Editor Panel */}
          <div className="lg:col-span-2 space-y-4">
            {/* Title & Excerpt */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h4 className="text-lg font-bold text-gray-900 mb-4">Article Details</h4>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-1">Title</label>
                  <input
                    type="text"
                    value={editedTitle}
                    onChange={(e) => setEditedTitle(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-900"
                    data-testid="article-title-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-1">Excerpt (Moniker)</label>
                  <select
                    value={editedExcerpt}
                    onChange={(e) => setEditedExcerpt(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-900 bg-white"
                    data-testid="article-excerpt-input"
                  >
                    <option value="">-- Select Moniker --</option>
                    <option value="&quot;We bridge the trust for tomorrow. Divine Intelligence Illuminates, Human Intelligence Leads, AI Intelligence Expands&copy;&quot; YAHFFIRMATIONS&copy; BY KOTF888&copy;">We bridge the trust for tomorrow...</option>
                    <option value="YAH HEALTH WEALTH HAPPINESS, The Universal Pillars Of Success YAHFFIRMATIONS&copy; BY KOTF888&copy;">YAH HEALTH WEALTH HAPPINESS...</option>
                    <option value="&quot;The Right of Authenticity, The Idea of Reliability, The Duty of Truth, a portal to success!&copy; = A.R.T Energi - Triad of Excellence&copy;&quot;, YAHFFIRMATIONS&copy; BY KOTF888&copy;">A.R.T Energi - Triad of Excellence...</option>
                    <option value="&quot;Its Time To Let Yah Spirit Sear It's Word on Yah Soul.&quot; YAHFFIRMATIONS&copy; BY KOTF888&copy;">Its Time To Let Yah Spirit Sear...</option>
                  </select>
                </div>

                {/* Image Selection */}
                {importedArticle.scraped_data.images.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">
                      Select Featured Image ({importedArticle.scraped_data.images.length} found)
                    </label>
                    <div className="grid grid-cols-4 gap-2">
                      {importedArticle.scraped_data.images.map((img, index) => (
                        <button
                          key={index}
                          onClick={() => setSelectedImage(img)}
                          className={`relative aspect-video rounded-lg overflow-hidden border-2 transition-all ${
                            selectedImage === img ? 'border-purple-500 ring-2 ring-purple-200' : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <img src={img} alt={`Option ${index + 1}`} className="w-full h-full object-cover" />
                          {selectedImage === img && (
                            <div className="absolute inset-0 bg-purple-500/20 flex items-center justify-center">
                              <CheckCircle className="text-white" size={24} />
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Content Editor */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-lg font-bold text-gray-900">Content Editor</h4>
                <button
                  onClick={() => setShowPreview(!showPreview)}
                  className="flex items-center space-x-2 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm text-gray-700 transition-colors"
                >
                  <Eye size={16} />
                  <span>{showPreview ? 'Edit' : 'Preview'}</span>
                </button>
              </div>

              {showPreview ? (
                <div 
                  className="prose prose-sm max-w-none p-4 bg-gray-50 rounded-lg min-h-[400px] overflow-auto text-gray-900"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(editedContent) }}
                />
              ) : (
                <textarea
                  value={editedContent}
                  onChange={(e) => setEditedContent(e.target.value)}
                  rows="20"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono text-sm text-gray-900"
                  data-testid="article-content-editor"
                />
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-4">
              <button
                onClick={() => handleSave(false)}
                disabled={saving}
                className="flex-1 flex items-center justify-center space-x-2 px-6 py-3 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-700 transition-colors disabled:opacity-50"
                data-testid="save-draft-button"
              >
                {saving ? <Loader2 className="animate-spin" size={20} /> : <Save size={20} />}
                <span>Save as Draft</span>
              </button>
              <button
                onClick={() => handleSave(true)}
                disabled={saving}
                className="flex-1 flex items-center justify-center space-x-2 px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg font-medium hover:shadow-lg transition-all disabled:opacity-50"
                data-testid="publish-button"
              >
                {saving ? <Loader2 className="animate-spin" size={20} /> : <CheckCircle size={20} />}
                <span>Publish Article</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!importedArticle && !loading && (
        <div className="bg-white rounded-xl shadow-lg p-12 text-center">
          <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Link2 className="text-purple-500" size={32} />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Import Your First Article</h3>
          <p className="text-gray-500 max-w-md mx-auto">
            Paste a LinkedIn article URL above to import content, analyze quality, and add template sections before publishing to your blog.
          </p>
        </div>
      )}
      </div>
      )}
    </div>
  );
};

export default ArticleImporter;
